import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import {
  Sparkles, Archive, Trash2, Phone, ChevronDown, ChevronUp,
  Loader2, RefreshCw, CircleDot, Check, X,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const BUDGET_LABELS = {
  "1-3m":      "1 – 3 Milyon TL",
  "3-5m":      "3 – 5 Milyon TL",
  "5-10m":     "5 – 10 Milyon TL",
  "10m+":      "10 Milyon TL+",
  "undecided": "Belirtilmedi",
};

const TYPE_LABELS = {
  villa:       "Villa / Müstakil Ev",
  apartment:   "Daire / Rezidans",
  commercial:  "Ticari Mekan",
  site:        "Site",
  renovation:  "Renovasyon / Tadilat",
  other:       "Diğer",
};

const AdminDreams = () => {
  const [dreams,   setDreams]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [busy,     setBusy]     = useState(null);
  const [notes,    setNotes]    = useState({});
  const [noteSave, setNoteSave] = useState({});

  const fetchDreams = useCallback(async () => {
    setLoading(true);
    let q = supabase.from("dream_requests").select("*").order("created_at", { ascending: false });
    if (filter === "unread")   q = q.eq("is_read", false).eq("is_archived", false);
    if (filter === "archived") q = q.eq("is_archived", true);
    if (filter === "all")      q = q.eq("is_archived", false);
    const { data } = await q;
    setDreams(data || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchDreams(); }, [fetchDreams]);

  const markRead = async (dream, read) => {
    setBusy(dream.id);
    await supabase.from("dream_requests").update({ is_read: read }).eq("id", dream.id);
    setDreams(prev => prev.map(d => d.id === dream.id ? { ...d, is_read: read } : d));
    setBusy(null);
  };

  const archive = async (dream) => {
    setBusy(dream.id);
    await supabase.from("dream_requests").update({ is_archived: true, is_read: true }).eq("id", dream.id);
    setDreams(prev => prev.filter(d => d.id !== dream.id));
    if (expanded === dream.id) setExpanded(null);
    setBusy(null);
  };

  const del = async (dream) => {
    if (!window.confirm(`"${dream.name}" adlı talebi kalıcı olarak silmek istiyor musunuz?`)) return;
    setBusy(dream.id);
    await supabase.from("dream_requests").delete().eq("id", dream.id);
    setDreams(prev => prev.filter(d => d.id !== dream.id));
    if (expanded === dream.id) setExpanded(null);
    setBusy(null);
  };

  const saveNotes = async (dream) => {
    setBusy(`notes_${dream.id}`);
    const noteText = notes[dream.id] ?? dream.notes ?? "";
    const { error } = await supabase.from("dream_requests").update({ notes: noteText }).eq("id", dream.id);
    setBusy(null);
    if (error) {
      setNoteSave(prev => ({ ...prev, [dream.id]: "error" }));
      setTimeout(() => setNoteSave(prev => ({ ...prev, [dream.id]: null })), 3000);
    } else {
      setDreams(prev => prev.map(d => d.id === dream.id ? { ...d, notes: noteText } : d));
      setNoteSave(prev => ({ ...prev, [dream.id]: "ok" }));
      setTimeout(() => setNoteSave(prev => ({ ...prev, [dream.id]: null })), 2000);
    }
  };

  const toggle = async (id) => {
    const dream = dreams.find(d => d.id === id);
    if (!dream) return;
    if (expanded !== id) {
      setExpanded(id);
      setNotes(prev => ({ ...prev, [id]: dream.notes || "" }));
      if (!dream.is_read) markRead(dream, true);
    } else {
      setExpanded(null);
    }
  };

  const FILTERS = [
    { id: "all",      label: "Gelen Kutusu" },
    { id: "unread",   label: "Okunmamış"    },
    { id: "archived", label: "Arşiv"         },
  ];

  const unreadCount = dreams.filter(d => !d.is_read).length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[#C5A059] text-[11px] tracking-[0.2em] uppercase mb-1 font-medium">Talepler</p>
          <h1 className="text-[#F5F5F7] text-2xl font-light">
            Hayalim Talepleri
            {unreadCount > 0 && filter !== "archived" && (
              <span className="ml-2 bg-[#C5A059] text-black text-[11px] font-bold px-2 py-0.5 rounded-full align-middle">
                {unreadCount} yeni
              </span>
            )}
          </h1>
        </div>
        <button
          onClick={fetchDreams}
          className="flex items-center gap-2 px-3 py-2 text-[#666] hover:text-[#C5A059] border border-white/8 hover:border-[#C5A059]/30 rounded-sm transition-colors text-xs"
          title="Yenile"
        >
          <RefreshCw size={13} />
          <span className="hidden sm:inline">Yenile</span>
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-0 border-b border-white/6 mb-5">
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => { setFilter(f.id); setExpanded(null); }}
            className={`px-4 sm:px-6 py-3 text-[13px] border-b-2 -mb-px transition-colors ${
              filter === f.id
                ? "text-[#C5A059] border-[#C5A059] font-medium"
                : "text-[#555] border-transparent hover:text-[#999]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#555] text-sm py-10">
          <Loader2 size={16} className="animate-spin text-[#C5A059]" />
          Yükleniyor...
        </div>
      ) : dreams.length === 0 ? (
        <div className="text-center py-20">
          <Sparkles size={32} className="text-[#2a2a2a] mx-auto mb-3" />
          <p className="text-[#555] text-sm">
            {filter === "archived" ? "Arşivde talep yok." : filter === "unread" ? "Okunmamış talep yok." : "Henüz talep gelmedi."}
          </p>
        </div>
      ) : (
        <div className="bg-[#0d0d0d] border border-white/6 overflow-hidden divide-y divide-white/5">
          {dreams.map((dream) => {
            const isExp   = expanded === dream.id;
            const isBusy  = busy === dream.id;
            const noteVal = notes[dream.id] ?? dream.notes ?? "";
            return (
              <div key={dream.id} className={`transition-colors ${!dream.is_read ? "bg-[#C5A059]/4" : ""}`}>

                {/* ── Row button ── */}
                <button
                  onClick={() => toggle(dream.id)}
                  className="w-full text-left px-4 sm:px-5 py-4 flex items-start sm:items-center gap-3 hover:bg-white/3 transition-colors"
                >
                  {/* Unread dot */}
                  <div className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5 sm:mt-0"
                    style={{ background: dream.is_read ? "transparent" : "#C5A059" }} />

                  <Sparkles size={16} className={`flex-shrink-0 mt-0.5 sm:mt-0 ${dream.is_read ? "text-[#333]" : "text-[#C5A059]"}`} />

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    {/* Name + date */}
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className={`text-[14px] leading-tight ${dream.is_read ? "text-[#888] font-light" : "text-[#F5F5F7] font-medium"}`}>
                        {dream.name}
                      </p>
                      <p className="text-[11px] text-[#444] tabular-nums whitespace-nowrap flex-shrink-0">
                        {fmtDate(dream.created_at)}
                      </p>
                    </div>
                    {/* Type + budget pills */}
                    <div className="flex flex-wrap gap-2 mt-1">
                      {dream.project_type && (
                        <span className="text-[11px] text-[#666] bg-white/4 px-2 py-0.5 rounded-sm">
                          {TYPE_LABELS[dream.project_type] || dream.project_type}
                        </span>
                      )}
                      {dream.budget && (
                        <span className="text-[11px] text-[#666] bg-white/4 px-2 py-0.5 rounded-sm">
                          {BUDGET_LABELS[dream.budget] || dream.budget}
                        </span>
                      )}
                      {dream.phone && (
                        <span className="text-[11px] text-[#555] flex items-center gap-1">
                          <Phone size={9} />{dream.phone}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Chevron */}
                  <div className="flex-shrink-0 text-[#444] ml-1">
                    {isExp ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </div>
                </button>

                {/* ── Expanded detail ── */}
                {isExp && (
                  <div className="px-4 sm:px-5 pb-5 pt-3 border-t border-white/5 bg-[#0a0a0a]">

                    {/* Info grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                      {[
                        { label: "Ad Soyad",    value: dream.name },
                        { label: "Telefon",     value: dream.phone,  link: `tel:${dream.phone}` },
                        { label: "E-posta",     value: dream.email,  link: `mailto:${dream.email}` },
                        { label: "Proje Tipi",  value: TYPE_LABELS[dream.project_type]  || dream.project_type  || "—" },
                        { label: "Bütçe",       value: BUDGET_LABELS[dream.budget]      || dream.budget        || "—" },
                        { label: "Konum",       value: dream.location || "—" },
                      ].map(({ label, value, link }) => (
                        <div key={label} className="bg-[#111] border border-white/5 px-4 py-3 rounded-sm">
                          <p className="text-[10px] tracking-widest text-[#555] uppercase mb-1.5">{label}</p>
                          {link && value && value !== "—" ? (
                            <a href={link} className="text-[14px] text-[#C5A059] hover:underline font-light break-all">{value}</a>
                          ) : (
                            <p className="text-[14px] text-[#F5F5F7] font-light">{value || "—"}</p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Description */}
                    {dream.description && (
                      <div className="mb-5">
                        <p className="text-[10px] tracking-widest text-[#555] uppercase mb-2">Hayalim Açıklaması</p>
                        <div className="bg-[#111] border-l-2 border-[#C5A059]/40 px-4 py-4 text-[13px] text-[#aaa] font-light leading-relaxed whitespace-pre-wrap rounded-sm">
                          {dream.description}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div className="mb-5">
                      <p className="text-[10px] tracking-widest text-[#555] uppercase mb-2">İç Notlar</p>
                      <div className="flex gap-2">
                        <textarea
                          value={noteVal}
                          onChange={e => setNotes(prev => ({ ...prev, [dream.id]: e.target.value }))}
                          rows={3}
                          placeholder="Bu talep hakkında notlar ekleyin..."
                          className="flex-1 bg-[#111] border border-white/8 text-[#F5F5F7] text-[13px] px-3 py-2.5 resize-none focus:outline-none focus:border-[#C5A059] transition-colors placeholder-[#333] rounded-sm"
                        />
                        <button
                          onClick={() => saveNotes(dream)}
                          disabled={!!busy}
                          className={`px-4 text-[12px] font-medium transition-colors self-stretch flex flex-col items-center justify-center gap-1 rounded-sm min-w-[72px] disabled:opacity-40 ${
                            noteSave[dream.id] === "ok"    ? "bg-green-900/30 text-green-400" :
                            noteSave[dream.id] === "error" ? "bg-red-900/30 text-red-400" :
                            "bg-[#C5A059]/10 hover:bg-[#C5A059]/20 text-[#C5A059]"
                          }`}
                          title="Kaydet"
                        >
                          {busy === `notes_${dream.id}` ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : noteSave[dream.id] === "ok" ? (
                            <><Check size={14} /><span>Kaydedildi</span></>
                          ) : noteSave[dream.id] === "error" ? (
                            <><X size={14} /><span>Hata</span></>
                          ) : (
                            <><Check size={14} /><span>Kaydet</span></>
                          )}
                        </button>
                      </div>
                    </div>

                    <p className="text-[11px] text-[#333] mb-5">{fmtDate(dream.created_at)}</p>

                    {/* Action buttons */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => markRead(dream, !dream.is_read)}
                        disabled={!!busy}
                        className="flex items-center gap-2 px-4 py-2 text-[13px] bg-white/5 hover:bg-white/10 text-[#999] hover:text-[#F5F5F7] border border-white/8 rounded-sm transition-colors disabled:opacity-40"
                      >
                        {isBusy ? <Loader2 size={13} className="animate-spin" />
                          : dream.is_read ? <CircleDot size={13} /> : <Check size={13} />}
                        {dream.is_read ? "Okunmadı İşaretle" : "Okundu İşaretle"}
                      </button>

                      {!dream.is_archived && (
                        <button
                          onClick={() => archive(dream)}
                          disabled={!!busy}
                          className="flex items-center gap-2 px-4 py-2 text-[13px] bg-white/5 hover:bg-white/10 text-[#999] hover:text-[#F5F5F7] border border-white/8 rounded-sm transition-colors disabled:opacity-40"
                        >
                          <Archive size={13} />
                          Arşivle
                        </button>
                      )}

                      <button
                        onClick={() => del(dream)}
                        disabled={!!busy}
                        className="flex items-center gap-2 px-4 py-2 text-[13px] bg-red-900/10 hover:bg-red-900/25 text-red-500/70 hover:text-red-400 border border-red-900/20 rounded-sm transition-colors disabled:opacity-40 ml-auto"
                      >
                        <Trash2 size={13} />
                        Sil
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDreams;
